import { latestAcceptedOffer, offerEmploymentTypes } from '../hr-workflow/model.js';
import { updatePersonSchema } from './person.js';
/**
 * The accepted offer of the CURRENT candidacy, from the workflow view the person page already reads. The
 * latest one, by the same helper every other offer screen uses, so the dialog cannot pick a different offer
 * from the Offer and stage section (ADR-011 section 4).
 */
export function activationOfferSource(view) {
    const accepted = latestAcceptedOffer(view.records, view.offerSteps, view.candidacySince);
    if (accepted === null)
        return { kind: 'none' };
    return accepted.kind === 'offer' ? { kind: 'portal', terms: accepted.terms } : { kind: 'paper', signedAt: accepted.signedAt };
}
/** The values the person's record already holds, as the dialog's fields. */
export function recordedActivationDetails(person) {
    return {
        positionTitle: person.assignment?.positionTitle?.trim() ?? '',
        department: person.assignment?.department?.trim() ?? '',
        employmentClassification: person.assignment?.employmentClassification ?? '',
        employmentType: person.assignment?.employmentType?.trim() ?? '',
        firstDay: person.hireDate === null ? '' : person.hireDate.slice(0, 10),
        servicePlaces: person.assignment?.servicePlaces ?? [],
    };
}
/** The offer's terms as the dialog's fields. The proposed start becomes the first day. */
export function offeredActivationDetails(terms) {
    return {
        positionTitle: terms.position,
        department: terms.department,
        employmentClassification: terms.employmentClassification,
        employmentType: terms.employmentType,
        firstDay: terms.proposedStartDate,
        servicePlaces: terms.servicePlaces ?? [],
    };
}
const isEmpty = (details, field) => {
    const value = details[field];
    return typeof value === 'string' ? value === '' : value.length === 0;
};
const sameDetail = (a, b, field) => field === 'servicePlaces'
    ? a.servicePlaces.length === b.servicePlaces.length && a.servicePlaces.every((place, i) => place.placeId === b.servicePlaces[i]?.placeId)
    : a[field] === b[field];
const FIELDS = ['positionTitle', 'department', 'employmentClassification', 'employmentType', 'firstDay', 'servicePlaces'];
/**
 * The dialog's opening values. THE OFFER WINS where it has a value (Julio, #764: "yes, fill from the offer"),
 * and the record's value is kept only where the offer has none (an old offer with no picked places, say). So a
 * rehired person's earlier job never wins over the new signed offer. A field where the record holds a different
 * value is reported in `differs`, so the dialog can show "The record says ..." beside it. HR checks every
 * value and can change either.
 */
export function fillActivationDetails(person, source) {
    const recorded = recordedActivationDetails(person);
    if (source.kind !== 'portal')
        return { details: recorded, recorded, filledFrom: [], differs: [], typedServiceArea: null };
    const offered = offeredActivationDetails(source.terms);
    const filledFrom = FIELDS.filter((field) => !isEmpty(offered, field));
    const differs = FIELDS.filter((field) => !isEmpty(recorded, field) && !isEmpty(offered, field) && !sameDetail(recorded, offered, field));
    // Every key of ActivationDetails is built here from the same two objects, so the object has exactly its shape.
    const details = Object.fromEntries(FIELDS.map((field) => [field, filledFrom.includes(field) ? offered[field] : recorded[field]]));
    const typed = source.terms.servicePlaces === undefined || source.terms.servicePlaces.length === 0 ? source.terms.serviceArea.trim() : '';
    return { details, recorded, filledFrom, differs, typedServiceArea: typed === '' ? null : typed };
}
/**
 * Whether saving these details changes something the readiness derivation reads, so the client's readiness
 * (worked out from the record BEFORE the save) can be wrong. Today that is the W-2 or 1099 only: it decides
 * which requirements apply (`personFacts`). The server works readiness out again after the update, so the
 * dialog must not hold the move back on the old answer.
 */
export function activationChangesReadiness(patch) {
    return patch.assignment?.employmentClassification !== undefined;
}
/** The schedule types the dialog offers: the three an offer carries, plus whatever the record already holds. */
export function scheduleChoices(current) {
    return current === '' || offerEmploymentTypes.includes(current) ? offerEmploymentTypes : [...offerEmploymentTypes, current];
}
/**
 * The sparse patch the dialog sends to the EXISTING `PATCH /api/people/{id}` (no new write path), carrying only
 * what HR's confirmed values change on the record. The server's own diff names the fields on the audit event,
 * never their values. Parsed with `updatePersonSchema`, so a value the schema refuses throws here and not later.
 *
 * The typed `assignment.serviceArea` is never sent: as in the Roster's staff dialog, it stays on the record until
 * places replace it on screen. A cleared field is sent as `null` (a cleared first day is refused by the dialog
 * before it gets here).
 */
export function activationPatch(person, confirmed) {
    const recorded = recordedActivationDetails(person);
    const changed = (field) => !sameDetail(recorded, confirmed, field);
    const text = (value) => (value.trim() === '' ? null : value.trim());
    const assignment = {
        ...(changed('positionTitle') ? { positionTitle: text(confirmed.positionTitle) } : {}),
        ...(changed('department') ? { department: text(confirmed.department) } : {}),
        ...(changed('employmentClassification') ? { employmentClassification: confirmed.employmentClassification === '' ? null : confirmed.employmentClassification } : {}),
        ...(changed('employmentType') ? { employmentType: text(confirmed.employmentType) } : {}),
        ...(changed('servicePlaces') ? { servicePlaces: [...confirmed.servicePlaces] } : {}),
    };
    return updatePersonSchema.parse({
        ...(Object.keys(assignment).length === 0 ? {} : { assignment }),
        // The same stored form the employee page writes for a first day (person-details-form).
        ...(changed('firstDay') ? { hireDate: confirmed.firstDay === '' ? null : `${confirmed.firstDay}T00:00:00.000Z` } : {}),
    });
}
//# sourceMappingURL=activation-fill.js.map